import * as React from "react"
import { cn } from "@/lib/utils"

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
    children: React.ReactNode
    variant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "body1" | "body2" | "caption" | "overline"
}

const variantStyles: Record<string, string> = {
    h1: "scroll-m-20 text-4xl font-bold tracking-tight lg:text-5xl",
    h2: "scroll-m-19 text-3xl font-semibold tracking-tight first:mt-0",
    h3: "scroll-m-16 text-2xl font-semibold tracking-tight",
    h4: "scroll-m-14 text-xl font-semibold tracking-tight",
    h5: "text-lg font-semibold tracking-tight",
    h6: "text-base font-semibold tracking-tight",
    body1: "text-base leading-7",
    body2: "text-sm leading-6",
    caption: "text-xs leading-5 text-muted-foreground",
    overline: "text-xs font-semibold uppercase tracking-widest text-muted-foreground",
}

const variantElements: Record<string, keyof React.ReactHTML> = {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    body1: "p",
    body2: "p",
    caption: "span",
    overline: "span",
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
    ({ className, variant = "body1", children, ...props }, ref) => {
        const Element = variantElements[variant] as React.ElementType
        return (
            <Element
                ref={ref}
                className={cn(variantStyles[variant], className)}
                {...props}
            >
                {children}
            </Element>
        )
    }
)

Typography.displayName = "Typography"

export { Typography, type TypographyProps }